import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const MessageDetailsModal = ({ 
  message, 
  isOpen = false, 
  onClose = () => {},
  onRetry = () => {},
  onContactClick = () => {},
  className = '' 
}) => {
  if (!isOpen || !message) return null;

  const getStatusColor = (status) => {
    switch (status) {
      case 'delivered':
        return 'text-success bg-success/10';
      case 'pending':
        return 'text-warning bg-warning/10';
      case 'failed':
        return 'text-error bg-error/10';
      default:
        return 'text-text-secondary bg-muted';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'delivered':
        return 'CheckCircle';
      case 'pending':
        return 'Clock';
      case 'failed':
        return 'XCircle';
      default:
        return 'Circle';
    }
  };

  const getChannelIcon = (channel) => {
    switch (channel) {
      case 'email':
        return 'Mail';
      case 'sms':
        return 'MessageSquare';
      case 'whatsapp':
        return 'MessageCircle';
      default:
        return 'Send';
    }
  };

  const formatFullTimestamp = (timestamp) => {
    return new Date(timestamp)?.toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 z-500 flex items-center justify-center p-4 bg-black/50">
      <div className={`bg-surface rounded-lg shadow-modal w-full max-w-2xl max-h-[90vh] overflow-y-auto ${className}`}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-text-primary">
            Message Details
          </h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Recipient Info */}
          <div className="flex items-center space-x-4">
            <button
              onClick={() => onContactClick(message?.recipient)}
              className="flex-shrink-0 hover-scale transition-micro"
            >
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center overflow-hidden">
                {message?.recipient?.avatar ? (
                  <img 
                    src={message?.recipient?.avatar} 
                    alt={message?.recipient?.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Icon name="User" size={24} className="text-text-secondary" />
                )}
              </div>
            </button>
            <div className="flex-1">
              <button
                onClick={() => onContactClick(message?.recipient)}
                className="text-lg font-medium text-text-primary hover:text-primary transition-micro block text-left"
              >
                {message?.recipient?.name}
              </button>
              <p className="text-text-secondary">
                {message?.recipient?.email || message?.recipient?.phone}
              </p>
            </div>
          </div>

          {/* Message Info Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Status */}
            <div className="bg-muted rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="Activity" size={16} className="text-text-secondary" />
                <span className="text-sm font-medium text-text-secondary">Status</span>
              </div>
              <div className={`inline-flex items-center space-x-2 px-3 py-1 rounded-full ${getStatusColor(message?.status)}`}>
                <Icon name={getStatusIcon(message?.status)} size={16} />
                <span className="font-medium capitalize">{message?.status}</span>
              </div>
            </div>

            {/* Channel */}
            <div className="bg-muted rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="Send" size={16} className="text-text-secondary" />
                <span className="text-sm font-medium text-text-secondary">Channel</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name={getChannelIcon(message?.channel)} size={16} className="text-primary" />
                <span className="font-medium capitalize">{message?.channel}</span>
              </div>
            </div>

            {/* Message Type */}
            <div className="bg-muted rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="Tag" size={16} className="text-text-secondary" />
                <span className="text-sm font-medium text-text-secondary">Type</span>
              </div>
              <span className="font-medium capitalize">{message?.type}</span>
            </div>

            {/* Timestamp */}
            <div className="bg-muted rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="Clock" size={16} className="text-text-secondary" />
                <span className="text-sm font-medium text-text-secondary">Sent</span>
              </div>
              <span className="font-medium">{formatFullTimestamp(message?.timestamp)}</span>
            </div>
          </div>

          {/* Message Content */}
          <div className="bg-muted rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-3">
              <Icon name="MessageSquare" size={16} className="text-text-secondary" />
              <span className="text-sm font-medium text-text-secondary">Message Content</span>
            </div>
            <div className="bg-surface border border-border rounded-lg p-4">
              <p className="text-text-primary whitespace-pre-wrap">
                {message?.content}
              </p>
            </div>
          </div>

          {/* Delivery Details */}
          {message?.deliveryDetails && (
            <div className="bg-muted rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-3">
                <Icon name="Info" size={16} className="text-text-secondary" />
                <span className="text-sm font-medium text-text-secondary">Delivery Details</span>
              </div>
              <div className="space-y-2">
                {message?.deliveryDetails?.attempts && (
                  <div className="flex justify-between">
                    <span className="text-text-secondary">Attempts:</span>
                    <span className="font-medium">{message?.deliveryDetails?.attempts}</span>
                  </div>
                )}
                {message?.deliveryDetails?.lastAttempt && (
                  <div className="flex justify-between">
                    <span className="text-text-secondary">Last Attempt:</span>
                    <span className="font-medium">{formatFullTimestamp(message?.deliveryDetails?.lastAttempt)}</span>
                  </div>
                )}
                {message?.deliveryDetails?.error && (
                  <div className="mt-2 p-3 bg-error/10 border border-error/20 rounded-lg">
                    <p className="text-error text-sm">{message?.deliveryDetails?.error}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Response */}
          {message?.response && (
            <div className="bg-muted rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-3">
                <Icon name="Reply" size={16} className="text-text-secondary" />
                <span className="text-sm font-medium text-text-secondary">Recipient Response</span>
              </div>
              <div className="bg-surface border border-border rounded-lg p-4">
                <p className="text-text-primary whitespace-pre-wrap">
                  {message?.response?.content}
                </p>
                <p className="text-text-secondary text-sm mt-2">
                  {formatFullTimestamp(message?.response?.timestamp)}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-between p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={() => onContactClick(message?.recipient)}
            iconName="User"
            iconPosition="left"
          >
            View Contact
          </Button>

          <div className="flex items-center space-x-3">
            {message?.status === 'failed' && (
              <Button
                variant="default"
                onClick={() => onRetry(message)}
                iconName="RotateCcw"
                iconPosition="left"
              >
                Retry Send
              </Button>
            )}
            <Button
              variant="ghost"
              onClick={onClose}
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessageDetailsModal;